import { useQuery } from '@tanstack/react-query';
import { useUser } from '@/stores';
import { useUIStore } from '@/stores/ui.store';
import { crmKeys } from '../../shared/constants/queryKeys';
import { usePublicScoringConfig } from '../../settings/hooks/useSettings';
import { businessLinesApi } from '../services/business-lines.service';

export function useBusinessLines(enabled = true) {
  return useQuery({
    queryKey: crmKeys.businessLines,
    queryFn: () => businessLinesApi.list(true),
    staleTime: 1000 * 60,
    enabled,
  });
}

/**
 * Business-line selection (v1.4 FR-37, RR-6).
 *
 * - Flag-gated: reads `enableInventory` from the narrow public-config endpoint
 *   (the full settings endpoint is admin-only, BUG-CRM-19) — flag OFF means no
 *   line fetch at all, so flag-OFF tenants see zero behavior change (FR-29).
 * - Persistence: `{userId, lineId}` in the persisted UI store; a stored
 *   selection from a DIFFERENT user is discarded (RR-6 shared-machine rule).
 * - Fallback: stored selection when still valid, else the default line, else
 *   the first line.
 */
export function useSelectedBusinessLine() {
  const user = useUser();
  const { data: config } = usePublicScoringConfig();
  const inventoryEnabled = config?.enableInventory ?? false;
  const { data: lines = [], isLoading } = useBusinessLines(inventoryEnabled);
  const selection = useUIStore((s) => s.businessLineSelection);
  const setSelection = useUIStore((s) => s.setBusinessLineSelection);

  const stored = selection && user && selection.userId === user.id ? selection.lineId : null;
  const validStored = stored && lines.some((l) => l.id === stored) ? stored : null;
  const selectedLineId = validStored ?? lines.find((l) => l.isDefault)?.id ?? lines[0]?.id ?? null;

  const selectLine = (lineId: string) => {
    if (user) {
      setSelection({ userId: user.id, lineId });
    }
  };

  return { inventoryEnabled, lines, isLoading, selectedLineId, selectLine };
}
